/**
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2016, 2019. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 */
/*global module require*/
"use strict";

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
Object.assign = Object.assign || require("object-assign");
var unresolvedRef = "__unresolved__";
var cyclicalRef = "__cycle__";
module.exports.getProp = getProp;
module.exports.setProp = setProp;
module.exports.parseTemplate = parseTemplate;
module.exports.unresolvedRef = unresolvedRef;
var TEMPLATE = /(\{\{.+?\}\})/; // group 1 captures {{foo}} including the curly braces
var TEMPLATE_INNER = /\{\{(.+?)\}\}/; // group 1 captures just `foo` from {foo}}

function parseTemplate(str) {
  if (typeof str !== "string") {
    return null;
  }
  var segments = str.split(TEMPLATE);
  if (segments.length <= 1) {
    return null;
  }
  var components = segments.map(function (seg) {
    var match;
    if ((match = TEMPLATE_INNER.exec(seg)) !== null) {
      return new Variable(match[1]);
    }
    return seg; // string
  });
  // Delete superfluous "" components from start and end
  if (components[0] === "") {
    components.shift();
  }
  if (components[components.length - 1] === "") {
    components.pop();
  }
  return new Template(components);
}

// Represents a template attached to a capability
// @param array components The template components. Each element is Variable|string
function Template(components) {
  this.components = components;
}
// Expands this template to a value
// @param {object} templateModel The complete template object
// @param {CapabilityModel} capabilities
// @param {function(variable) -> object} specialExpander A function that can bypass normal resolution
// logic for certain variables.
// @param {map<string -> object>} seen
Template.prototype.expand = function (capabilities, specialExpander, seen) {
  seen = seen || Object.create(null);
  var expandedComponents = this.components.map(function (component) {
    if (typeof component === "string") {
      return component;
    }
    // component is a Variable
    // First check if specialExpander has a value for it
    var value = specialExpander(component);
    if (value !== null && typeof value !== 'undefined') {
      return value;
    }

    // Resolve expression `foo.bar.parameters.baz`: 0th segment is the top-level property
    // (generally form or services), subsequent segments are a lookup within it
    var segs = component.expression.split(".");
    if (segs.length < 2) {
      // Reference to top-level property of the template structure?
      var data = capabilities.getTopLevelData();
      if (!data) {
        return "";
      }
      return data[segs[0]];
    }

    // TODO need a more general mustache engine, this is becoming awkward
    var first = segs[0];
    var capability;
    if (first === "toolchain") {
      capability = capabilities.getToolchain();
      segs = segs.slice(1);
    } else if (first === "template") {
      capability = capabilities.getTemplate();
      segs = segs.slice(1);
    } else if (first === "services" || first === "form") {
      capability = capabilities.getByLabel(segs[1]);
      segs = segs.slice(2);
    } else if (first === "linkToolchain") {
      capability = capabilities.getLinkToolchain();
      segs = segs.slice(1);
    } else {
      // no capability model entity match, so
      // reconstruct the original mustache
      // template and return as-is...
      return "{{" + segs.join(".") + "}}";
    }

    // Everything following the initial segment(s) is the lookup path
    var path = segs.join(".");
    var template = findTemplate(capability, path);
    if (!capability || !template) {
      return getProp(capability, path);
    }
    // Recursive case: `foo.parameters.bar` where bar is a templated param inside capability foo.
    //  Expand bar's template recursively, using `seen` to prevent cycles.
    if (seen[component.expression]) {
      return cyclicalRef;
    }
    seen[component.expression] = true;
    return template.expand(capabilities, specialExpander, seen);
  });

  // Single component template, eg. `{{fizz.buzz}}`
  if (expandedComponents.length === 1) {
    return expandedComponents[0];
  }

  // Multi-component template, eg. `foo{{bar.baz}}qux`: implicitly coerce to a single string
  return expandedComponents.join("");
};
function Variable(expr) {
  this.expression = expr;
}

// Given obj and property expression `a.b.c`, returns obj.a.b.c
function getProp(obj, propExpr) {
  return propExpr.split(".").reduce(function (prev, curr) {
    if (prev === null || typeof prev === "undefined") {
      return unresolvedRef;
    }
    return prev[curr];
  }, obj);
}

// Given obj and property expression `a.b.c`, sets obj.a.b.c equal to `value`, creating any missing objects
// along the way.
function setProp(obj, propExpr, value) {
  var segs = propExpr.split(".");
  var propName;
  for (var i = 0; i < segs.length - 1; i++) {
    propName = segs[i];
    if (!obj[propName] || _typeof(obj[propName]) !== "object") {
      obj[propName] = {};
    }
    obj = obj[propName];
  }
  obj[segs[segs.length - 1]] = value;
}
function findTemplate(capability, paramPath) {
  if (!capability || !capability.templates) {
    return null;
  }
  return capability.templates[paramPath] || null;
}